import { NetSuiteSyncInvoicesResponseSchema, NetSuiteGetSubsidiariesResponseSchema } from '@weft/api-definition';
import type { NetSuiteSyncInvoicesResponse, NetSuiteSyncInvoicesRequestPayload, NetSuiteGetSubsidiariesResponse } from '@weft/api-definition';
import type { NetSuiteConnectRequestPayload, NetSuiteExportSalesOrderRequestPayload, NetSuiteGetCustomerResponse, NetSuiteGetCustomersResponse, NetSuiteGetProductsResponse } from '@weft/api-definition';
import { INTEGRATION_PROVIDERS } from '@weft/types';
import { isEmptyArray, isNotEmptyString, isNotNullOrUndefined, isSingleItem, purge, validateNotEmptyString, withDefault } from '@weft/utils';
import { getPrivateApi } from 'apps/app/src/services/api';
import type { QueryClient, UseMutationOptions } from 'react-query';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { CustomerNotSyncedNetsuiteWarning } from '../../components/new-design/feedback/warnings/CustomerNotSyncedNetsuiteWarning';
import { toastFromPromise, userMessage } from '../../utils/vy-toasts';
import { useActivatedIntegration } from '../use-activated-integration';
import type { CustomUseQueryOptions } from './requests-types/custom-use-query-options';
import { USE_INVOICES_OVERVIEWS_QUERY_KEY } from './use-invoices-overviews/use-invoices-overviews-consts';

export function useNetSuiteConnect(
  options?: UseMutationOptions<void, unknown, NetSuiteConnectRequestPayload>) {
  return useMutation((netSuiteConnectRequest) => {
    const api = getPrivateApi();

    return api.netSuiteConnect({ netSuiteConnectRequest });
  }, options);
}

export function useNetSuiteProducts(options?: CustomUseQueryOptions<NetSuiteGetProductsResponse>) {
  const isNetSuiteEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.netSuite, { suspense: true });

  const isEnabledInOptions = withDefault(options?.enabled, true);
  const enabled = isEnabledInOptions && isNetSuiteEnabled;

  return useQuery(
    ['netSuiteProducts'],
    () => {
      const api = getPrivateApi();

      return api.netSuiteGetProducts();
    },
    {
      ...options,
      enabled,
    },
  );
}

export function useCanConnectNetSuite() {
  return useActivatedIntegration(INTEGRATION_PROVIDERS.netSuite, { suspense: true });
}

export function useNetSuiteCustomers(searchParam?: string, options?: CustomUseQueryOptions<NetSuiteGetCustomersResponse>) {
  const isNetSuiteActivated = useCanConnectNetSuite();

  return useQuery(
    purge(['netSuiteCustomers', searchParam]),

    () => {
      const api = getPrivateApi();

      return api.netSuiteGetCustomers({ search: searchParam });
    },
    {
      ...options,
      enabled: isNetSuiteActivated,
    },
  );
}

export function useNetSuiteCustomer(customerId?: string, options?: CustomUseQueryOptions<NetSuiteGetCustomerResponse>) {
  return useQuery(
    ['netSuiteCustomer'],
    () => {
      const api = getPrivateApi();

      return isNotNullOrUndefined(customerId) ? api.netSuiteGetCustomer({ customerId }) : null;
    },
    options,
  );
}

export function useSyncNetSuiteInvoice(options?: UseMutationOptions<NetSuiteSyncInvoicesResponse, unknown, NetSuiteSyncInvoicesRequestPayload>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (netSuiteSyncInvoicesRequest) => {
      const api = getPrivateApi();
      const syncingInvoices = api.netSuiteSyncInvoices({ netSuiteSyncInvoicesRequest });
      const netSuiteSyncInvoiceResponse = await toastFromPromise(syncingInvoices, { pending: 'Syncing to NetSuite...' });
      const parsedResponse = NetSuiteSyncInvoicesResponseSchema.parse(netSuiteSyncInvoiceResponse);

      handleNetSuiteSyncInvoicesResponses(parsedResponse);

      return parsedResponse;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([USE_INVOICES_OVERVIEWS_QUERY_KEY]);
    },
    ...options,
  });
}

export function useExportNetSuiteSalesOrder(contractId: string, options?: UseMutationOptions<void, unknown, NetSuiteExportSalesOrderRequestPayload>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (netSuiteExportSalesOrderRequest) => {
      const api = getPrivateApi();

      return api.exportNetSuiteSalesOrder({ netSuiteExportSalesOrderRequest });
    },
    onSuccess: async () => {
      await invalidateContractPages(contractId, queryClient);
    },
    onError: async () => {
      await invalidateContractPages(contractId, queryClient);
    },
    ...options,
  });
}

export function useUpdateNetSuiteSalesOrder(contractId: string, options?: UseMutationOptions<void, unknown, NetSuiteExportSalesOrderRequestPayload>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (netSuiteUpdateSalesOrderRequest) => {
      const api = getPrivateApi();

      return api.netSuiteUpdateSalesOrder({ netSuiteUpdateSalesOrderRequest });
    },
    onSuccess: async () => {
      await invalidateContractPages(contractId, queryClient);
    },
    onError: async () => {
      await invalidateContractPages(contractId, queryClient);
    },
    ...options,
  });
}

async function invalidateContractPages(contractId: string, queryClient: QueryClient) {
  await queryClient.invalidateQueries(['contractPage', contractId]);
  await queryClient.invalidateQueries(['contracts', 'contract']);
}

function handleNetSuiteSyncInvoicesResponses({ invoicesSyncAttemptsResults }: NetSuiteSyncInvoicesResponse) {
  const failedAttempts = invoicesSyncAttemptsResults.filter(({ errorReason }) => isNotNullOrUndefined(errorReason));

  if (isEmptyArray(failedAttempts)) {
    userMessage('success', 'Invoices synced to NetSuite');

    return;
  }

  if (isSingleItem(failedAttempts)) {
    userMessage('error', CustomerNotSyncedNetsuiteWarning, { failedAttempt: failedAttempts[0] });

    return;
  }

  userMessage('warning', `Failed to sync ${failedAttempts.length} out of ${invoicesSyncAttemptsResults.length} invoices`);
}

export function useNetSuiteUsers(erpId: string | undefined, options?: CustomUseQueryOptions<NetSuiteGetSubsidiariesResponse>) {
  const queryKey = getNetSuiteUsersQueryKey(erpId);
  const queryFunction = getNetSuiteUsersQueryFunction(erpId);

  const isNetSuiteActivated = useCanConnectNetSuite();

  const isActivatedInOptions = withDefault(options?.enabled, true);
  const enabled = isNotEmptyString(erpId) && isNetSuiteActivated && isActivatedInOptions;

  return useQuery(
    queryKey,
    queryFunction,
    {
      ...options,
      enabled,
    },
  );
}

export function prefetchNetSuiteUsers(erpId: string, queryClient: QueryClient) {
  const queryKey = getNetSuiteUsersQueryKey(erpId);
  const queryFunction = getNetSuiteUsersQueryFunction(erpId);

  return queryClient.prefetchQuery(queryKey, queryFunction);
}

function getNetSuiteUsersQueryKey(erpId: string | undefined) {
  return purge(['subsidiaries', erpId]);
}

function getNetSuiteUsersQueryFunction(erpId: string | undefined) {
  return async () => {
    const api = getPrivateApi();
    validateNotEmptyString(erpId, 'erpId');
    const getSubsidiariesResponse = await api.getNetSuiteSubsidiaries({ erpId }, {});

    return NetSuiteGetSubsidiariesResponseSchema.parse(getSubsidiariesResponse);
  };
}